/**
 * StepLockProgress.tsx — Step 2: Lock confirmation + progress polling
 *
 * Shows the "Gemini calls" warning + Lock button on first paint.
 * After Lock is pressed:
 *   - POST /api/campaigns/:id/lock
 *   - Switch to the SummoningAnimation (the same one used during a single
 *     carousel generation in DistillPageV2). Reusing this animation reframes
 *     the long lock wait as "we're crafting your campaign" rather than a
 *     blocked button labelled "Locking…".
 *   - Poll GET /api/campaigns/:id every 3s; show a quiet "X of Y ready"
 *     counter below the animation so progress is still visible.
 *   - When the campaign leaves the generation states, fire onComplete().
 *
 * RI-006 applied: the animation stays visible between poll ticks — no flicker.
 */

import React, { useState, useEffect, useRef } from "react";
import {
  primaryBtn, stepHeading, stepSub, spinnerStyle, ACCENT,
} from "../DistillPageV2/sharedStyles";
import SummoningAnimation from "../../components/SummoningAnimation";
import type { Campaign, CampaignPost } from "./types";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  campaign:    Campaign;
  onLock:      () => Promise<void>;     // calls POST /api/campaigns/:id/lock
  onPollTick:  () => Promise<Campaign>; // calls GET  /api/campaigns/:id
  onComplete:  (updated: Campaign) => void;
  isLocking:   boolean;
  lockError:   string | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 3000;

/** Rotating messages shown during campaign lock — campaign-flavoured riffs on
 *  the same crafting messages used in single-carousel generation. */
const CAMPAIGN_CRAFTING_MSGS = [
  "Drafting your captions…",
  "Designing carousel slides…",
  "Threading the narrative…",
  "Layering ideas into slides…",
  "Adding the finishing touches…",
  "Almost ready to publish…",
];

const MSG_ROTATE_MS = 2400;

// ── Component ─────────────────────────────────────────────────────────────────

const StepLockProgress: React.FC<Props> = ({
  campaign, onLock, onPollTick, onComplete, isLocking, lockError,
}) => {
  const [posts,           setPosts]           = useState<CampaignPost[]>(campaign.posts ?? []);
  const [locked,          setLocked]          = useState(false);
  const [craftingMessage, setCraftingMessage] = useState(CAMPAIGN_CRAFTING_MSGS[0]);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const msgRef  = useRef<ReturnType<typeof setInterval> | null>(null);

  const totalDays  = campaign.storyArc?.length ?? campaign.totalDays;
  const readyCount = posts.filter(p => p.status === 'pending').length;

  // ── Cleanup intervals on unmount ────────────────────────────────────────
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (msgRef.current)  clearInterval(msgRef.current);
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (msgRef.current)  { clearInterval(msgRef.current);  msgRef.current  = null; }
  };

  const startPolling = () => {
    // Rotate the crafting message every MSG_ROTATE_MS so the animation feels alive
    let msgIdx = 0;
    msgRef.current = setInterval(() => {
      msgIdx = (msgIdx + 1) % CAMPAIGN_CRAFTING_MSGS.length;
      setCraftingMessage(CAMPAIGN_CRAFTING_MSGS[msgIdx]);
    }, MSG_ROTATE_MS);

    // Poll the campaign every POLL_INTERVAL_MS until lock completes
    pollRef.current = setInterval(async () => {
      try {
        const updated = await onPollTick();
        setPosts(updated.posts ?? []);
        const GENERATING_STATUSES = ['draft', 'arc_locked', 'generating', 'rendering'];
        const allReady =
          (updated.posts?.length ?? 0) > 0 &&
          !GENERATING_STATUSES.includes(updated.status);
        if (allReady) {
          stopPolling();
          onComplete(updated);
        }
      } catch {
        // Poll errors are transient — keep trying
      }
    }, POLL_INTERVAL_MS);
  };

  const handleLock = () => {
    // Flip to the animation IMMEDIATELY — onLock takes 30–60s to resolve
    // (Gemini calls run synchronously inside it). Waiting for it would leave
    // the user staring at the warning + button. Polling can run in parallel:
    // GET /campaigns/:id reflects the live status while the POST is in flight.
    setLocked(true);
    startPolling();

    // Fire the lock request in the background. If it rejects, revert.
    onLock().catch(() => {
      // The orchestrator has already set lockError. Stop polling and reveal
      // the pre-lock view again so the error banner renders.
      stopPolling();
      setLocked(false);
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  // Once Lock is pressed: full-bleed summoning experience.
  if (locked) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
        <div style={{ textAlign: "center", marginBottom: "8px" }}>
          <div style={stepHeading}>Building your campaign</div>
          <div style={stepSub}>
            Crafting captions and carousel slides for all {totalDays} days.
          </div>
        </div>

        <SummoningAnimation message={craftingMessage} />

        {/* Subtle progress counter — keeps real-progress visible without competing
            with the animation. */}
        <div
          data-testid="cp-lock-progress"
          style={{
            fontSize:    "13px",
            fontWeight:  600,
            color:       readyCount === totalDays ? "#2DB88F" : ACCENT,
            display:     "flex",
            alignItems:  "center",
            gap:         "8px",
            marginTop:   "4px",
          }}
        >
          {readyCount < totalDays && <span style={spinnerStyle} />}
          {readyCount} of {totalDays} posts ready
        </div>
      </div>
    );
  }

  // Pre-lock state: warning + Lock button (unchanged behaviour).
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div>
        <div style={stepHeading}>Lock campaign</div>
        <div style={stepSub}>
          Review the arc one last time. Once locked you can't edit the arc, but you can pause, skip, or delete posts from the dashboard.
        </div>
      </div>

      <div style={{
        background:   "#FFFBEB",
        border:       "1.5px solid #F59E0B",
        borderRadius: "12px",
        padding:      "14px 16px",
        fontSize:     "14px",
        lineHeight:   "1.6",
        color:        "#92400E",
      }}>
        ⚠️ Locking makes <strong>{totalDays * 2} Gemini calls</strong> ({totalDays} captions
        + {totalDays} carousels) and schedules {totalDays} LinkedIn posts.
        You can still pause, skip, or delete the campaign afterwards — but you can't go back to editing the arc.
      </div>

      <button
        data-testid="cp-lock-confirm-btn"
        onClick={handleLock}
        style={{
          ...primaryBtn,
          justifyContent: "center",
          width:   "100%",
          padding: "14px",
          fontSize: "15px",
        }}
      >
        🔒 Lock Campaign
      </button>

      {lockError && (
        <div style={{
          background: "#FFF0F0", border: "1.5px solid #E84F4F",
          borderRadius: "12px", padding: "14px 16px",
          fontSize: "14px", color: "#C0392B",
        }}>
          {lockError}
        </div>
      )}
    </div>
  );
};

export default StepLockProgress;
